import { useAuth } from "@dl/web-core-js/auth";
import { columns as defineColumns, DataTable, useServerTable } from "@dl/web-core-js/table";
import { Badge, cn, Switch } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { PartDTO, UnitDTO } from "../../api/manufacturing";
import {
  PURCHASE_ORDER_STATUSES,
  PurchaseOrderLineClient,
  type PurchaseOrderLineDTO,
  type PurchaseOrderStatus,
} from "../../api/purchasing";
import { isoDate } from "../../api/reporting";
import { SupplierLink } from "../../components/SupplierLink";
import { StatusBadge, useStatusLabel } from "../purchasing/badges";
import { formatPrice, formatQuantity } from "../purchasing/replenishment/shared";

/** The statuses under which a line is still expected: out with the supplier, or waiting to be. */
const OPEN_STATUSES: readonly PurchaseOrderStatus[] = ["AWAITING_APPROVAL", "ISSUED"];

/**
 * "Still to come" as filters the server understands. Outstanding is a range
 * from just above zero, since the server's ranges include their bounds.
 */
const OPEN_FILTERS: Record<string, string[]> = {
  order_status: [...OPEN_STATUSES],
  quantity_outstanding: ["0.0001", ""],
};

/** Open lines are few, even for a board bought weekly; one request holds them all. */
const OPEN_LINE_LIMIT = 500;

function isOpen(row: PurchaseOrderLineDTO): boolean {
  return row.order_status != null && OPEN_STATUSES.includes(row.order_status) && Number(row.quantity_outstanding ?? 0) > 0;
}

/** When the line is due: the supplier's promise when there is one, else what we asked for. */
function expectedDate(row: PurchaseOrderLineDTO): string | null {
  return row.promised_date ?? row.required_date ?? null;
}

/**
 * Every purchase order line of one part, across orders, paged in the database:
 * the part's buying history, and what is still to come.
 *
 * <p>The strip on top answers the question people open the tab with, "is any
 * on its way, and when", from the open lines alone; the grid underneath is the
 * whole history, newest order first, and the switch narrows it to the open
 * lines. Quantities are in the unit the line was ordered in; the strip converts
 * to the part's stock unit so lines in boxes and lines in units add up.</p>
 *
 * <p>Expense lines carry no part, so they never appear here.</p>
 */
export function PartPurchasesTab({ part, units, stockUnitCode }: { part: PartDTO; units: UnitDTO[]; stockUnitCode: string | null }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const lines = useMemo(() => new PurchaseOrderLineClient(http), [http]);
  const [openOnly, setOpenOnly] = useState(false);
  const today = isoDate(new Date());
  const statusLabel = useStatusLabel();
  const unitCode = useMemo(() => {
    const byId = new Map(units.map((unit) => [unit.id, unit.code]));
    return (row: PurchaseOrderLineDTO) => (row.unit_id === null ? stockUnitCode : (byId.get(row.unit_id) ?? stockUnitCode)) ?? "";
  }, [units, stockUnitCode]);

  // Pinning narrows every request rather than filtering what came back.
  const pinned: Record<string, string[]> = { part_id: [String(part.id)], ...(openOnly ? OPEN_FILTERS : {}) };
  const table = useServerTable<PurchaseOrderLineDTO>({
    queryKey: ["purchasing", "purchase-order-line", "part", part.id, openOnly ? "open" : "all"],
    fetchPage: (request) => lines.find({ ...request, filters: { ...(request.filters ?? {}), ...pinned } }),
  });

  const open = useQuery({
    queryKey: ["purchasing", "purchase-order-line", "part", part.id, "open-summary"],
    queryFn: () =>
      lines.find({
        page: 0,
        size: OPEN_LINE_LIMIT,
        filters: { part_id: [String(part.id)], ...OPEN_FILTERS },
        sort: ["order_date,asc"],
      }),
  });

  const summary = useMemo(() => {
    const rows = (open.data?.content ?? []).filter(isOpen);
    const quantity = rows.reduce((sum, row) => sum + Number(row.quantity_outstanding ?? 0) * Number(row.stock_units_per_unit || 1), 0);
    const orders = new Set(rows.map((row) => row.purchase_order_id)).size;
    // Late lines and the next arrival are separate facts: an order SIGM never
    // closed, due last year, must not hide the truck due next week.
    const dates = rows
      .map(expectedDate)
      .filter((date): date is string => date !== null)
      .sort();
    const late = dates.filter((date) => date < today);
    const next = dates.find((date) => date >= today) ?? null;
    return { quantity, orders, next, lateCount: late.length, lateSince: late[0] ?? null };
  }, [open.data, today]);

  const cols = useMemo(() => {
    const c = defineColumns<PurchaseOrderLineDTO>();
    const late = (row: PurchaseOrderLineDTO) => {
      const date = expectedDate(row);
      return isOpen(row) && date !== null && date < today;
    };
    return [
      c.custom(
        "order_number",
        (row) => row.order_number ?? null,
        (row) => (
          <Link
            to="/purchasing/purchase-orders/$orderId"
            params={{ orderId: String(row.purchase_order_id) }}
            onClick={(event) => event.stopPropagation()}
            className="tabular-nums text-accent hover:underline"
          >
            {row.order_number ?? `#${row.purchase_order_id}`}
          </Link>
        ),
        { headerKey: "column.po.number", filter: "range", priority: 1 },
      ),
      c.text("order_date", (row) => row.order_date ?? "", { headerKey: "column.po.order-date", filter: "daterange", priority: 3 }),
      c.custom(
        "order_status",
        (row) => row.order_status ?? "",
        (row) => (row.order_status ? <StatusBadge status={row.order_status} /> : null),
        {
          headerKey: "column.status",
          filter: "multiselect",
          filterOptions: PURCHASE_ORDER_STATUSES.map((status) => ({ value: status, label: statusLabel(status) })),
          priority: 2,
        },
      ),
      c.custom(
        "supplier_name",
        (row) => row.supplier_name ?? "",
        (row) => (
          <SupplierLink id={row.supplier_id} className="truncate">
            {row.supplier_name ?? ""}
          </SupplierLink>
        ),
        { headerKey: "column.po.supplier", priority: 1 },
      ),
      c.custom(
        "quantity",
        (row) => row.quantity,
        (row) => (
          <span className="tabular-nums">
            {formatQuantity(row.quantity)} <span className="text-ink-muted">{unitCode(row)}</span>
          </span>
        ),
        { headerKey: "column.quantity", filter: "range", align: "right", priority: 2 },
      ),
      // Computed per row from the ledger, so neither filtered nor sorted.
      c.custom(
        "quantity_received",
        (row) => row.quantity_received ?? null,
        (row) => <span className="tabular-nums">{formatQuantity(row.quantity_received ?? 0)}</span>,
        { headerKey: "column.po.received", filter: "none", enableSorting: false, align: "right", priority: 3 },
      ),
      c.custom(
        "quantity_outstanding",
        (row) => row.quantity_outstanding ?? null,
        (row) => {
          const outstanding = Number(row.quantity_outstanding ?? 0);
          // Outstanding on a closed or cancelled order is not coming; shown faint, not as a promise.
          return (
            <span className={cn("tabular-nums", outstanding <= 0 ? "text-ink-faint" : isOpen(row) ? "font-semibold text-ink" : "text-ink-faint line-through")}>
              {formatQuantity(outstanding)}
            </span>
          );
        },
        { header: t("part.purchases.outstanding", "Outstanding"), filter: "range", align: "right", priority: 2 },
      ),
      c.custom(
        "quantity_cancelled",
        (row) => row.quantity_cancelled,
        (row) => <span className="tabular-nums">{row.quantity_cancelled ? formatQuantity(row.quantity_cancelled) : ""}</span>,
        { headerKey: "column.po.cancelled", filter: "none", enableSorting: false, align: "right", defaultVisible: false },
      ),
      c.custom(
        "unit_price",
        (row) => row.unit_price,
        (row) => (
          <span className="tabular-nums">
            {formatPrice(row.unit_price)} <span className="text-[11px] text-ink-muted">{row.currency ?? ""}</span>
          </span>
        ),
        { headerKey: "column.po.price", filter: "range", align: "right", priority: 3 },
      ),
      // The supplier's promise wins over what was asked for, as in planning;
      // sorted and filtered on the required date, which every line has.
      c.custom(
        "required_date",
        (row) => row.required_date ?? "",
        (row) => {
          const date = expectedDate(row);
          if (date === null) return null;
          return (
            <span className="inline-flex items-center gap-1.5 tabular-nums">
              <span className={cn(late(row) && "text-risk")}>{date}</span>
              {row.promised_date && (
                <span
                  className="text-[11px] text-ink-muted"
                  title={row.required_date ? t("part.purchases.required-was", "Required {{date}}", { date: row.required_date }) : undefined}
                >
                  {t("part.purchases.promised", "promised")}
                </span>
              )}
              {late(row) && (
                <Badge size="sm" variant="error">
                  {t("part.purchases.late", "Late")}
                </Badge>
              )}
            </span>
          );
        },
        { header: t("part.purchases.expected", "Expected"), filter: "daterange", priority: 3 },
      ),
      c.text("supplier_part_number", (row) => row.supplier_part_number ?? "", {
        headerKey: "po.line.supplier-number",
        filter: "none",
        enableSorting: false,
        defaultVisible: false,
      }),
    ];
  }, [t, today, statusLabel, unitCode]);

  const unit = stockUnitCode ?? "";

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-line bg-surface px-4 py-3 lg:px-6">
        {open.isLoading ? (
          <span className="text-[12px] text-ink-muted">{t("common.loading", "Loading…")}</span>
        ) : open.isError ? (
          <span className="text-[12px] text-risk">{t("part.purchases.open-error", "The open orders could not be loaded.")}</span>
        ) : summary.orders === 0 ? (
          <span className="text-[13px] text-ink-muted">{t("part.purchases.none-open", "Nothing on order.")}</span>
        ) : (
          <>
            <span className="text-[13px] text-ink">
              {t("part.purchases.on-order", "On order: {{quantity}} {{unit}} in {{count}} open orders", {
                quantity: formatQuantity(summary.quantity),
                unit,
                count: summary.orders,
              })}
            </span>
            {summary.next !== null && (
              <span className="text-[13px] text-ink-muted">{t("part.purchases.next", "Next expected {{date}}", { date: summary.next })}</span>
            )}
            {summary.lateCount > 0 && (
              <span className="text-[13px] text-risk">
                {t("part.purchases.late-lines", "{{count}} lines late, the oldest due {{date}}", {
                  count: summary.lateCount,
                  date: summary.lateSince,
                })}
              </span>
            )}
          </>
        )}
        <Switch
          className="ml-auto"
          checked={openOnly}
          onChange={setOpenOnly}
          label={t("part.purchases.open-only", "Open only")}
        />
      </div>

      {table.error ? (
        <p className="px-4 py-3 text-[13px] text-risk lg:px-6">{t("part.purchases.error", "The purchase history could not be loaded.")}</p>
      ) : (
        <DataTable<PurchaseOrderLineDTO>
          data={table.rows}
          columns={cols}
          getRowId={(row) => String(row.id)}
          stateKey="/manufacturing/parts/purchases"
          loading={table.loading}
          server={table.mode}
          initialSorting={[{ id: "order_date", desc: true }]}
          rowClassName={(row) => (isOpen(row) ? "bg-accent-soft/40" : undefined)}
          emptyMessage={
            openOnly
              ? t("part.purchases.empty-open", "No open order for this part.")
              : t("part.purchases.empty", "This part has never been ordered.")
          }
        />
      )}
    </div>
  );
}
